import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useInstanceDataspacesStore } from "../i-dataspace";
import { useDomainStore } from "../domain";
import { useSymbolsStore } from "../symbols";

export type SuggestionModel =
	| "GPT 5-mini"
	| "Mistral Medium"
	| "Mistral Large"
	| "GPT 5.1"
	| "Claude Haiku 4.5"
	| "LR-Grapefruit";

export type SuggestionModelSource = "user" | "dataspace" | "global";

export const useConfigStore = defineStore("alternis-config", () => {
	const dataspaceStore = useInstanceDataspacesStore();
	const domainStore = useDomainStore();
	const symbolsStore = useSymbolsStore();
	const ALTERNIS_HOST = "https://notitia-alternis.livereader.com";
	const DEFAULT_SUGGESTION_MODEL = "GPT 5-mini";
	const suggestionModels = ref<SuggestionModel[]>([
		"Mistral Medium",
		"Mistral Large",
		"GPT 5-mini",
		"Claude Haiku 4.5",
		"GPT 5.1",
		"LR-Grapefruit",
	]);

	const playSynth = ref<boolean>(read("playSynth", true));
	const playTranslation = ref<boolean>(read("playTranslation", false));
	const playTranslationPlayback = ref<boolean>(read("playTranslationPlayback", true));
	const holdToRecord = ref<boolean>(read("hold2Record", true));
	// Nested structure: { [domainId]: { [dataspaceId]: SuggestionModel } }
	const userSuggestionModels = ref<Record<string, Record<string, SuggestionModel>>>(
		read("suggestionModelsByDomainAndDataspace", {}),
	);
	const wordHighlighting = ref<boolean>(read("wordHighlighting", false));

	const dataspaceSuggestionModel = computed(() => {
		const envModel = dataspaceStore.environmentVariables["SUGGESTION_MODEL"];
		if (envModel && suggestionModels.value.includes(envModel as SuggestionModel)) {
			return envModel as SuggestionModel;
		}
		return null;
	});

	function getSuggestionModel(domainId: string, dataspaceId: string): SuggestionModel {
		const userModel = userSuggestionModels.value[domainId]?.[dataspaceId];
		if (userModel && suggestionModels.value.includes(userModel) && domainStore.isAdmin) {
			return userModel;
		}
		if (dataspaceSuggestionModel.value) return dataspaceSuggestionModel.value;
		return DEFAULT_SUGGESTION_MODEL;
	}

	function getSuggestionModelSourceText(domainId: string, dataspaceId: string): string {
		let source: SuggestionModelSource;
		const userModel = userSuggestionModels.value[domainId]?.[dataspaceId];
		if (userModel && suggestionModels.value.includes(userModel) && domainStore.isAdmin) {
			source = "user";
		} else if (dataspaceSuggestionModel.value) {
			source = "dataspace";
		} else {
			source = "global";
		}
		switch (source) {
			case "user":
				return symbolsStore.GUILabel("91b7caa2-e440-4bdd-a94f-6a07cdc9f526");
			case "dataspace":
				return symbolsStore.GUILabel("0210b186-4fa0-43c3-bdbd-f272516a85a0");
			case "global":
				return symbolsStore.GUILabel("cac57462-7a23-4315-91d0-23a8635402ee");
		}
	}

	function setUserSuggestionModel(domainId: string, dataspaceId: string, model: SuggestionModel) {
		if (!userSuggestionModels.value[domainId]) {
			userSuggestionModels.value[domainId] = {};
		}
		userSuggestionModels.value[domainId][dataspaceId] = model;
	}

	function resetUserSuggestionModel(domainId: string, dataspaceId: string) {
		if (userSuggestionModels.value[domainId]) {
			delete userSuggestionModels.value[domainId][dataspaceId];
			// Clean up empty domain entries
			if (Object.keys(userSuggestionModels.value[domainId]).length === 0) {
				delete userSuggestionModels.value[domainId];
			}
		}
	}

	watch(playSynth, (val) => save("playSynth", val));
	watch(playTranslation, (val) => save("playTranslation", val));
	watch(playTranslationPlayback, (val) => save("playTranslationPlayback", val));
	watch(holdToRecord, (val) => save("hold2Record", val));
	watch(userSuggestionModels, (val) => save("suggestionModelsByDomainAndDataspace", val), { deep: true });
	watch(wordHighlighting, (val) => save("wordHighlighting", val));

	return {
		ALTERNIS_HOST,
		playSynth,
		playTranslation,
		playTranslationPlayback,
		holdToRecord,
		userSuggestionModels,
		suggestionModels,
		wordHighlighting,
		dataspaceSuggestionModel,
		defaultSuggestionModel: DEFAULT_SUGGESTION_MODEL,
		getSuggestionModel,
		getSuggestionModelSourceText,
		setUserSuggestionModel,
		resetUserSuggestionModel,
	};
});

function save(key: string, value: any) {
	localStorage.setItem(key, JSON.stringify(value));
}
function read(key: string, def: any): any {
	const value = localStorage.getItem(key);
	if (!value) return def;
	return JSON.parse(value);
}
